/**
 * Funciones puras de cálculo para el dashboard "Control de Costos AWS".
 * Sin dependencias de Vue — se pueden probar de forma aislada con datos de ejemplo.
 */
import {
  TIPO_CAMBIO_GTQ_USD,
  COSTO_AWS,
  COSTO_OTP_POR_CANAL,
  PLANES_PDF_COMERCIAL,
  MODELO_INTERNO,
} from './tarifasReconocimiento';

/**
 * Costo AWS estimado (piso, no techo — ver banner de limitación en la vista).
 * Rekognition + Face Liveness se aplican solo a gestiones COMPLETADAS (perfil fijo
 * de 9 llamadas). El costo de OTP usa el mix REAL observado ese mes, no un mix asumido.
 *
 * @param {number} gestionesCompletadas
 * @param {{email:number, whatsapp:number, sms:number}} conteoOtpPorCanal
 */
export function calcularCostoAwsEstimado({
  gestionesCompletadas,
  conteoOtpPorCanal,
}) {
  const rekognitionUSD =
    gestionesCompletadas *
    COSTO_AWS.llamadasRekognitionPorGestion *
    COSTO_AWS.rekognitionImagenPorLlamada;
  const livenessUSD = gestionesCompletadas * COSTO_AWS.faceLivenessPorCheck;

  const otpUSD = Object.keys(COSTO_OTP_POR_CANAL).reduce((acc, canal) => {
    const conteo = conteoOtpPorCanal?.[canal] || 0;
    return acc + conteo * COSTO_OTP_POR_CANAL[canal];
  }, 0);

  const cloudwatchUSD = COSTO_AWS.cloudWatchFijoMensual;
  const totalUSD = rekognitionUSD + livenessUSD + otpUSD + cloudwatchUSD;
  const costoPromedioPorGestionUSD =
    gestionesCompletadas > 0 ? totalUSD / gestionesCompletadas : 0;

  return {
    rekognitionUSD,
    livenessUSD,
    otpUSD,
    cloudwatchUSD,
    totalUSD,
    costoPromedioPorGestionUSD,
  };
}

/**
 * Facturación de un plan específico del PDF comercial: tarifa base + excedente
 * si el volumen de evaluaciones INICIADAS supera el límite de ese plan.
 */
function calcularFacturaPorPlan(gestionesIniciadas, plan) {
  const excedente = Math.max(0, gestionesIniciadas - plan.limiteEvaluaciones);
  const totalGTQ = plan.tarifaBaseGTQ + excedente * plan.tarifaAdicionalGTQ;
  return {
    plan: plan.nombre,
    limiteEvaluaciones: plan.limiteEvaluaciones,
    excedente,
    totalGTQ,
    totalUSD: totalGTQ / TIPO_CAMBIO_GTQ_USD,
  };
}

/**
 * Facturación bajo el modelo del PDF comercial, para LOS TRES planes a la vez
 * (no se asume cuál está contratado, ya que el usuario decidió comparar sin
 * decidir el modelo vigente). Marca el plan más barato como "recomendado".
 *
 * @param {number} gestionesIniciadas — volumen del periodo, incluye abandonadas
 */
export function calcularFacturaPdfComercial(gestionesIniciadas) {
  const porTier = PLANES_PDF_COMERCIAL.map((plan) =>
    calcularFacturaPorPlan(gestionesIniciadas, plan)
  );
  const recomendado = porTier.reduce(
    (min, actual) => (actual.totalGTQ < min.totalGTQ ? actual : min),
    porTier[0]
  );
  return { porTier, recomendado };
}

/**
 * Facturación bajo el modelo interno: plano hasta el límite base, luego
 * tramos de excedente escalonados. Si excede el último tramo, extrapola con
 * la tarifa del último tramo y marca requiereRevisionContrato.
 *
 * @param {number} gestionesCompletadas — volumen del periodo, solo completadas
 */
export function calcularFacturaModeloInterno(gestionesCompletadas) {
  const { baseUSD, limiteBase, tramos } = MODELO_INTERNO;

  if (gestionesCompletadas <= limiteBase) {
    return {
      tramoAlcanzado: 'base',
      totalUSD: baseUSD,
      totalGTQ: baseUSD * TIPO_CAMBIO_GTQ_USD,
      requiereRevisionContrato: false,
    };
  }

  let excedente = gestionesCompletadas - limiteBase;
  let totalUSD = baseUSD;
  let tramoAlcanzado = 'base';

  for (const tramo of tramos) {
    const tamanoTramo = tramo.hasta - tramo.desde + 1;
    const aplicable = Math.min(excedente, tamanoTramo);
    if (aplicable > 0) {
      totalUSD += aplicable * tramo.tarifaPorGestionUSD;
      excedente -= aplicable;
      tramoAlcanzado = `${tramo.desde}-${tramo.hasta}`;
    }
  }

  let requiereRevisionContrato = false;
  if (excedente > 0) {
    const ultimoTramo = tramos[tramos.length - 1];
    totalUSD += excedente * ultimoTramo.tarifaPorGestionUSD;
    tramoAlcanzado = `> ${ultimoTramo.hasta} (extrapolado, requiere revisión de contrato)`;
    requiereRevisionContrato = true;
  }

  return {
    tramoAlcanzado,
    totalUSD,
    totalGTQ: totalUSD * TIPO_CAMBIO_GTQ_USD,
    requiereRevisionContrato,
  };
}
